import { useEffect, useMemo } from 'react';

import { AxiosResponse } from 'axios';
import { UseInfiniteQueryResult } from 'react-query';

interface PaginatedList<T> {
  results: T[];
}

interface UseAllPagesResult<T> {
  items: T[];
  isLoading: boolean;
  isSuccess: boolean;
}

/**
 * Eagerly fetch every page of an infinite query and return the flattened results.
 *
 * Use for bounded lists that must be fully loaded at once (e.g. the Assign Rates
 * grid), where the API page cap would otherwise truncate the data and leave the
 * UI working with a partial set. `isLoading` stays true and `isSuccess` stays
 * false until the last page has been fetched.
 */
export default function useAllPages<T>(
  query: UseInfiniteQueryResult<AxiosResponse<PaginatedList<T>>>,
): UseAllPagesResult<T> {
  const { data, isLoading, isSuccess, hasNextPage, fetchNextPage, isFetchingNextPage } = query;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const items = useMemo(() => data?.pages.map(page => page.data.results).flat() ?? [], [data]);

  return {
    items,
    isLoading: isLoading || Boolean(hasNextPage),
    isSuccess: isSuccess && !hasNextPage,
  };
}
